import React from 'react'
import { connect } from 'react-redux'

import { Option } from '@src/components/form-controls/select-input'

import { TokenInterface } from '@store/actionSlices/token'
import { DEFAULT_SHOWCASE_ROOM, SortOrder } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

import { SelectInput } from '@components/form-controls'

import {
  selectShowcaseRoomsFromResult,
  useGetShowcaseRoomsQuery,
} from '@api/showcase-room'

interface RoomInputProps {
  projectName: string
  token: TokenInterface
  room: string
  value: string
  onChange: (value: string) => void
  error?: string
}

function RoomInput({
  projectName,
  token,
  room,
  value,
  onChange,
  error,
}: RoomInputProps) {
  const { isLoaded, showcaseRooms } = useGetShowcaseRoomsQuery(
    { projectName, pagination: false, sort_order: SortOrder.ASC },
    { skip: !token?._id, selectFromResult: selectShowcaseRoomsFromResult }
  )

  const checkDefaultRoom = React.useCallback(
    (name: string) => name.toLocaleLowerCase() === DEFAULT_SHOWCASE_ROOM,
    []
  )

  const defaultRoom = React.useMemo(
    () => showcaseRooms.find((item) => checkDefaultRoom(item.name)),
    [showcaseRooms, checkDefaultRoom]
  )

  const isValidSelectedRoom = React.useMemo<boolean>(
    () =>
      showcaseRooms.some((item) => {
        if (checkDefaultRoom(item.name)) {
          return room === ''
        }
        return item.name === room
      }),
    [showcaseRooms, room, checkDefaultRoom]
  )

  const roomOptions = React.useMemo<Option[]>(() => {
    const options = showcaseRooms.map<Option>((item) => ({
      label: item.name,
      value: item.name,
    }))
    if (!isValidSelectedRoom) {
      options.unshift({
        label: 'Please select a room',
        value: '',
        hidden: true,
      })
    }
    return options
  }, [showcaseRooms, isValidSelectedRoom])

  const selectInputValue = React.useMemo(() => {
    if (value === '') {
      return defaultRoom?.name || ''
    }
    return value
  }, [value, defaultRoom])

  return (
    <SelectInput
      className={`${isLoaded ? '' : 'animate-pulse'} py-3`}
      name="room"
      label="Room"
      options={isLoaded ? roomOptions : []}
      value={selectInputValue}
      onChange={onChange}
      error={error}
    />
  )
}

const mapStateToProps = ({
  token,
  projectIdentity,
  appConfig: { room },
}: RootStateTypeExtra) => ({
  token,
  projectName: projectIdentity.projectName,
  room,
})

export default connect(mapStateToProps)(RoomInput)
